'use client';

/**
 * Installs global listeners that recover from stale Next.js chunk load failures
 * by forcing a full reload. Targets iOS Safari "TypeError: Load failed" after
 * deploys when users navigate from onboarding into newly loaded routes.
 *
 * The capture-phase `error` listener is the load-bearing one: a `<script>` that
 * fails to load fires a resource error whose target carries the failing chunk
 * URL, which is the only signal that distinguishes a stale chunk from any other
 * failed fetch on Safari.
 */

import { useEffect } from 'react';
import {
  getErrorEventMessage,
  getScriptElementSrc,
  recoverFromChunkLoadFailure
} from '@/lib/utils/chunk-load-recovery';

function handleWindowError(event: ErrorEvent): void {
  recoverFromChunkLoadFailure(event.error, {
    message: getErrorEventMessage(event),
    sourceUrl: getScriptElementSrc(event.target)
  });
}

function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  recoverFromChunkLoadFailure(event.reason);
}

export function ChunkLoadRecoveryProvider({
  children
}: {
  children: React.ReactNode;
}) {
  useEffect(() => {
    window.addEventListener('error', handleWindowError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleWindowError, true);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, []);

  return children;
}
